import * as Track from "@axon-enterprise/track";

import {
  TEMP_MX_PROPERTIES,
  mixpanelService,
  TEMP_MX_EVENTS,
} from "../common/tracking/MixpanelService";
import { ERROR, SUCCESS } from "../utils/constants";
import { logger } from '../utils/logger';

import { getAppConfig } from './config';
import { CORE_SETUP_KEYS } from './localStorage';



// Create component logger for SessionUtils
const sessionUtilsLogger = logger.withComponent('SessionUtils');

export interface SessionCreateRequest {
  serial_number: string;
  captcha_token: string;
  source_ip: string;
}

export interface SessionCreateResponse {
  session_id: string;
  expires_at: string;
  expires_in_minutes: number;
}

/**
 * Get a localStorage item with TTL validation
 */
export const getValidLocalStorageItem = (key: string): string | null => {
  try {
    const item = localStorage.getItem(key);
    if (!item) return null;
    
    const parsedItem = JSON.parse(item);
    const now = Date.now();
    
    if (parsedItem.expiresAt && parsedItem.expiresAt < now) {
      localStorage.removeItem(key);
      return null;
    }
    
    return parsedItem.value || null;
  } catch (error) {
    localStorage.removeItem(key);
    return null;
  }
};

/**
 * Store localStorage item with TTL
 */
export const storeLocalStorageItem = (key: string, value: string): void => {
  const ttlMinutes = parseInt(process.env.REACT_APP_SESSION_TTL_MINUTES || '60', 10);
  const expirationTime = Date.now() + (ttlMinutes * 60 * 1000);
  
  try {
    localStorage.setItem(key, JSON.stringify({
      value,
      expiresAt: expirationTime
    }));
  } catch (error) {
    sessionUtilsLogger.warn(`Failed to store ${key} in localStorage`, error instanceof Error ? error : new Error(String(error)));
  }
};

/**
 * Reset expiration time for all core_setup_device* items
 */
export const resetExpirationTimes = (): void => {
  const ttlMinutes = parseInt(process.env.REACT_APP_SESSION_TTL_MINUTES || '60', 10);
  const expirationTime = Date.now() + (ttlMinutes * 60 * 1000);
  
  // Reset expiration for all existing core_setup_device* items
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('core_setup_device')) {
      try {
        const item = localStorage.getItem(key);
        if (item) {
          const parsedItem = JSON.parse(item);
          if (parsedItem.value) {
            localStorage.setItem(key, JSON.stringify({
              value: parsedItem.value,
              expiresAt: expirationTime
            }));
          }
        }
      } catch (error) {
        sessionUtilsLogger.warn(`Failed to reset expiration for ${key}`, error instanceof Error ? error : new Error(String(error)));
      }
    }
  });
};

/**
 * Check for valid session
 */
export const checkForValidSession = (): boolean => {
  const sessionId = getValidLocalStorageItem(CORE_SETUP_KEYS.SESSION_ID);
  const serialNumber = getValidLocalStorageItem(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER);
  
  return !!(sessionId && serialNumber);
};

/**
 * Create a new session via API call
 */
export const createSession = async (
  recaptchaToken: string,
  onSuccess?: () => void,
  onError?: (error: string) => void
): Promise<void> => {
  const config = getAppConfig();
  
  // Check for valid serial number in localStorage
  const serialNumber = getValidLocalStorageItem(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER);
  if (!serialNumber) {
    const errorMessage = 'We couldn\'t recognize your device. Please try scanning the QR code again. Don\'t have the QR code?';
    onError?.(errorMessage);
    return;
  }

  try {
    // Get client IP (we'll use a placeholder since we can't get real IP on frontend)
    const sourceIp = '0.0.0.0'; // This should be handled by backend

    const requestBody: SessionCreateRequest = {
      serial_number: serialNumber,
      captcha_token: recaptchaToken,
      source_ip: sourceIp
    };

    const response = await fetch(`${config.apiBaseUrl}/api/v1/session`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: 'Unknown error occurred' }));
      throw new Error(errorData.message || `HTTP ${response.status}: ${response.statusText}`);
    }

    const sessionData: SessionCreateResponse = await response.json();
    
    // Store session data
    storeLocalStorageItem(CORE_SETUP_KEYS.SESSION_ID, sessionData.session_id);
    storeLocalStorageItem(CORE_SETUP_KEYS.DEVICE_SERIAL_NUMBER, serialNumber);
    
    // Reset expiration times for all core_setup_device* items
    resetExpirationTimes();
    
    // Track successful session creation
    mixpanelService.track(TEMP_MX_EVENTS.SessionCreated, {
      [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serialNumber,
      [Track.MxPropertyName.Status]: SUCCESS,
    });
    
    onSuccess?.();
    
  } catch (error) {
    // eslint-disable-next-line no-console
    sessionUtilsLogger.error('Session creation failed:', error);
    
    const errorMessage = error instanceof Error ? error.message : 'An unexpected error occurred. Please try again.';
    
    // Track failed session creation
    mixpanelService.track(TEMP_MX_EVENTS.SessionCreationFailed, {
      [TEMP_MX_PROPERTIES.ApplianceSerialNumber]: serialNumber || 'unknown',
      [Track.MxPropertyName.Status]: ERROR,
      [Track.MxPropertyName.ErrorReason]: errorMessage,
    });
    
    onError?.(errorMessage);
  }
}; 